import React, {useEffect, useMemo, useRef, useState} from 'react';

import {Box, Typography} from '@mui/material';

import _ from 'lodash';

import dayjs from 'dayjs';

import FilterHeader from './FilterHeader';

import '../../../StandardReports.scss';

import {componentRegistry} from '../ComponentRegistry';

import {Config} from '../../../config/reportTypes';

import ArrAccordion from '../../../../../common/arr-accordion/ArrAccordion';

import LoadInputType from '../LoadInputType';

import ArrSourceSelectedSection from './ArrSourceSelectedSection';

import {SourceItem} from '../../../../tally/selectSourceContainer/ArrSourceSelectedContainer';

import {ISelectItem} from '../../../../../../shared/types';

import {ArrToggle} from '../../../../../common/arr-toggle/ArrToggle';

type ResolvedReportParams = {
  fromDateTime?: any;
  toDateTime?: any;
  selectedReviewers?: any;
  sourceType?: any;
  sourceID?: any;
  [key: string]: any;
};

import ArrRadio from '../../../../../common/arr-radio/ArrRadio';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import classNames from 'classnames';
import ArrToastNotification from '../../../../../common/arr-toast-notification/ArrToastNotification';
import {ReportTypeKey, ToastType} from '../../../../../../shared/constants';
import {t} from 'i18next';
import ArrTooltipWrapper from '../../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../../assets/images/info-icon.svg';
import utc from 'dayjs/plugin/utc';

type FilterSectionProps = {
  reportsConfig: Config;
  onAction?: (actionKey: string, values: Record<string, any>) => void;
  resetSignal?: boolean;
  sources?: SourceItem[];
  initialSelectedSources?: Record<string, any>;
  disableSourceSelection?: boolean;
  selectedOption: ISelectItem | null;
  onChange?: (values: Record<string, any>) => void;
  validateActionFromParent?: boolean;
  resolvedReportParams?: ResolvedReportParams;
  // To disable/enable continue update of values state variable base on reportConfig update
  observReportConfigUpdate?: boolean;
  selectedFilterValues?: Record<string, any>;
  resetDatePicker?: boolean;
  skipDateValidation?: boolean;
};

export const FilterSection: React.FC<FilterSectionProps> = ({
  reportsConfig,
  onAction,
  resetSignal,
  sources = [],
  initialSelectedSources,
  disableSourceSelection = false,
  selectedOption,
  onChange,
  validateActionFromParent = false,
  resolvedReportParams,
  observReportConfigUpdate = true,
  selectedFilterValues,
  resetDatePicker = false,
  skipDateValidation = false,
}) => {
  const isInitialReportConfigSet = useRef<boolean>(false);
  const loadedDailyHourlyRef = useRef<string | null>(null);
  const previousSelectedFilterValuesRef = useRef<
    Record<string, any> | undefined
  >(undefined);
  const [values, setValues] = useState<Record<string, any>>({});
  const [toastMessage, setToastMessage] = useState<string>();

  let DATE_FORMAT;
  switch (selectedOption?.value) {
    case ReportTypeKey.LOGIN_ACTIVITY:
    case ReportTypeKey.TIMELINE_AND_GAPS:
      DATE_FORMAT = 'YYYY/MM/DD';
      break;

    default:
      DATE_FORMAT = 'YYYY/MM/DD HH:mm:ss';
      break;
  }

  /** Normalize into strict YYYY/MM/DD (empty string if not parsable) */
  const normalizeStrict = (d: any) => {
    const s = String(d ?? '').trim();
    if (!s) return '';
    if (dayjs.utc(s, DATE_FORMAT, true).isValid()) return s;
    const parsed = dayjs.utc(s);
    return parsed.isValid() ? parsed.format(DATE_FORMAT) : '';
  };

  useEffect(() => {
    if (isInitialReportConfigSet.current && !observReportConfigUpdate) {
      return;
    }

    const allComps = reportsConfig.sections
      .flatMap((s) => s.filters)
      .flatMap((f) => f.components);

    const initialValues: Record<string, any> = {};

    allComps.forEach((comp) => {
      if (comp.key) {
        initialValues[comp.key] = comp.initialValues ?? null;
      }
    });

    reportsConfig.selectionCriteria?.fields?.forEach((f) => {
      if (f.key) {
        initialValues[f.key] = f.value ?? null;
      }
    });

    // Check if selectedFilterValues has changed (new report ID scenario)
    // Compare by content only, not reference, since getAppliedFilterValueObject always creates new objects
    const selectedFilterValuesChanged =
      selectedFilterValues !== undefined &&
      (previousSelectedFilterValuesRef.current === undefined ||
        JSON.stringify(selectedFilterValues) !==
          JSON.stringify(previousSelectedFilterValuesRef.current));

    // If selectedFilterValues is provided, always use it when it changes or on initial setup
    // This ensures new report IDs populate correctly
    if (selectedFilterValues !== undefined) {
      if (!isInitialReportConfigSet.current || selectedFilterValuesChanged) {
        isInitialReportConfigSet.current = true;
        previousSelectedFilterValuesRef.current = selectedFilterValues;
        setValues(selectedFilterValues);
      }
    } else if (!isInitialReportConfigSet.current) {
      // Initial setup without selectedFilterValues
      isInitialReportConfigSet.current = true;
      setValues(initialValues);
    } else if (observReportConfigUpdate) {
      // When observReportConfigUpdate is true, merge initialValues but preserve existing user changes
      // Only update keys that are null/undefined or don't exist, don't overwrite existing values
      setValues((prev) => {
        const updated = {...prev};
        let hasChanges = false;

        Object.keys(initialValues).forEach((key) => {
          if (
            initialValues[key] !== null &&
            initialValues[key] !== undefined &&
            (!(key in updated) ||
              updated[key] === null ||
              updated[key] === undefined)
          ) {
            updated[key] = initialValues[key];
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    }
  }, [
    reportsConfig,
    selectedFilterValues,
    observReportConfigUpdate,
    JSON.stringify(reportsConfig),
  ]);

  const [selectedSourcesBySection, setSelectedSourcesBySection] = useState<
    Record<string, any[] | null>
  >({});

  function mapTypeToSectionId(t: string) {
    const key = t.trim().toLowerCase();

    if (key.includes('security')) return 'securityGroups';
    if (key.includes('reviewer')) return 'reviewers';
    if (key.includes('search')) return 'searches';
    if (key.includes('assignment')) return 'assignments';
    if (key.includes('folder')) return 'folders';

    const found = sources.find(
      (s) => s.label?.trim()?.toLowerCase() === key || s.id === key
    );

    return found?.id ?? key;
  }

  function toArray(v: any) {
    return Array.isArray(v) ? v.map(String) : [String(v)];
  }

  function meta(sectionId: string) {
    switch (sectionId) {
      case 'securityGroups':
        return {
          sourceName: 'SECURITY GROUPS',
          navigationSourceType: 'SECGROUP',
        };
      case 'reviewers':
        return {sourceName: 'REVIEWERS', navigationSourceType: 'REVIEWER'};
      case 'searches':
        return {sourceName: 'SEARCHES', navigationSourceType: 'SEARCH'};
      case 'assignments':
        return {sourceName: 'ASSIGNMENTS', navigationSourceType: 'ASSIGNMENT'};
      case 'folders':
        return {sourceName: 'FOLDERS', navigationSourceType: 'FOLDER'};
      default:
        return {
          sourceName: sectionId?.toUpperCase?.() ?? '',
          navigationSourceType: sectionId?.toUpperCase?.() ?? '',
        };
    }
  }

  const initialSelectedBySectionFromResolved = useMemo(() => {
    const type = resolvedReportParams?.sourceType;
    const ids = resolvedReportParams?.sourceID;

    if (!type || ids === undefined || ids === null) return undefined;

    const sectionId = mapTypeToSectionId(String(type));
    const sourceIds = toArray(ids);

    return {[sectionId]: sourceIds};
  }, [
    resolvedReportParams?.sourceType,
    resolvedReportParams?.sourceID,
    sources,
  ]);

  /** Reset */
  useEffect(() => {
    if (resetSignal) {
      const keepDefaultDates = reportsConfig.sections.some((section) =>
        section.filters.some((filter) =>
          filter.components.some(
            (comp) => comp.key === 'usersList' || comp.key === 'timeOffset'
          )
        )
      );
      if (keepDefaultDates) {
        const next: Record<string, any> = {};
        reportsConfig.sections.forEach((section) => {
          section.filters.forEach((filter) => {
            filter.components.forEach((comp) => {
              if (comp.input_type !== 'datePicker') return;
              const initial = String(
                comp.initialValues || comp.initialStartDate || ''
              ).trim();
              if (initial) next[comp.key] = initial;
            });
          });
        });
        setValues(next);
      } else {
        setValues({});
      }

      setSelectedSourcesBySection({});
    }
  }, [resetSignal, reportsConfig]);

  /** Prefill From/To (strict strings) */
  useEffect(() => {
    if (!resolvedReportParams) return;

    const fromNorm = normalizeStrict(resolvedReportParams.fromDateTime);
    const toNorm = normalizeStrict(resolvedReportParams.toDateTime);

    const allComps = reportsConfig.sections
      .flatMap((s) => s.filters)
      .flatMap((f) => f.components);

    const fromComp: any = allComps.find(
      (c: any) =>
        typeof c?.label === 'string' && c.label.trim().toLowerCase() === 'from'
    );

    const toComp: any = allComps.find(
      (c: any) =>
        typeof c?.label === 'string' && c.label.trim().toLowerCase() === 'to'
    );

    const next: Record<string, any> = {};

    if (fromComp?.key) next[fromComp.key] = fromNorm;
    if (toComp?.key) next[toComp.key] = toNorm;

    // Optional combined range key (if your JSON defines one)
    const dateRangeComp: any = allComps.find(
      (c: any) =>
        typeof c?.key === 'string' && c.key.trim().toLowerCase() === 'daterange'
    );

    if (dateRangeComp?.key) {
      next[dateRangeComp.key] = `${fromNorm} - ${toNorm}`;
    }

    setValues((prev) => {
      const merged = {...prev, ...next};

      return merged;
    });
  }, [resolvedReportParams, reportsConfig]);

  /** Users prefill (stable booleans aligned to childLabels) */
  useEffect(() => {
    const selected = resolvedReportParams?.selectedReviewers;

    if (!selected) return;

    const usersComp: any = reportsConfig.sections
      .flatMap((s) => s.filters)
      .flatMap((f) => f.components)
      .find((c) => c.key === 'users' && c.input_type === 'checkboxList');

    const labels = Array.isArray(usersComp?.childLabels)
      ? usersComp.childLabels.map((x: any) => x?.label)
      : [];

    if (!labels.length) {
      return;
    }

    const selectedLabels: string[] = Array.isArray(selected)
      ? selected
          .map((x: any) => {
            if (typeof x === 'string') return x;
            if (typeof x === 'object') return x.userName ?? x.label ?? '';
            if (typeof x === 'number') return String(x);
            return '';
          })
          .filter(Boolean)
      : [];

    const initialBooleans = labels.map((lbl: string) =>
      selectedLabels.includes(lbl)
    );

    setValues((prev) => {
      if (Array.isArray(prev.users) && prev.users.length > 0) {
        return prev;
      }

      const merged = {...prev, users: initialBooleans};

      return merged;
    });
  }, [reportsConfig, resolvedReportParams?.selectedReviewers]);

  /**
   * Per-style Field/Text/New Line defaults (and their option lists), read from the exportData section's config.
   */
  const exportFormatConfig = useMemo(() => {
    const exportDataFilter = reportsConfig.sections
      .flatMap((s) => s.filters)
      .find((f) => f.id === 'exportData');

    const defaultValues: Record<string, any> = {};
    const dropdownLists: Record<string, any[]> = {};

    exportDataFilter?.components.forEach((c: any) => {
      if (c.input_type === 'exportFormatMenu') {
        defaultValues[c.key] = c.selectedValue ?? null;
        dropdownLists[c.key] = c.dropdownItem ?? [];
      }
    });

    return {defaultValues, dropdownLists};
  }, [reportsConfig]);

  /** Normalize dates in onChange */
  const handleChange = (key: string, value: any) => {
    const k = String(key ?? '')
      .trim()
      .toLowerCase();

    setValues((prev) => {
      let nextValue = value;

      if (k === 'fromdate' || k === 'todate') {
        if (Array.isArray(value)) {
          const fromPart = normalizeStrict(value?.[0]);
          const toPart = normalizeStrict(value?.[1]);

          nextValue = k === 'fromdate' ? fromPart : toPart;
        } else {
          nextValue = normalizeStrict(value);
        }
      }

      const merged = {...prev, [key]: nextValue};

      // Style drives the delimiter convention, so switching it invalidates any custom Field/Text/New Line picks.
      const styleActuallyChanged =
        k === 'style' &&
        String(prev?.style?.id ?? '') !== String(value?.id ?? '');

      if (styleActuallyChanged) {
        const findById = (list: any[], id: any) =>
          list?.find((item: any) => String(item?.id) === String(id)) ?? null;

        merged.field =
          findById(
            exportFormatConfig.dropdownLists.field,
            value?.fieldDefault
          ) ??
          exportFormatConfig.defaultValues.field ??
          null;
        merged.text =
          findById(exportFormatConfig.dropdownLists.text, value?.textDefault) ??
          exportFormatConfig.defaultValues.text ??
          null;
        merged.newLine =
          findById(
            exportFormatConfig.dropdownLists.newLine,
            value?.newLineDefault
          ) ??
          exportFormatConfig.defaultValues.newLine ??
          null;
      }

      return merged;
    });
  };

  /** Button enabled/disabled logic */
  const applyAction = reportsConfig.selectionCriteria.actions.find(
    (action) => action.label === 'Apply'
  );

  const resetAction = reportsConfig.selectionCriteria.actions.find(
    (action) => action.label === 'Reset'
  );

  const [isApplyDisabled, setIsApplyDisabled] = useState<boolean>(
    applyAction?.disabled ?? true
  );

  const [isResetDisabled, setIsResetDisabled] = useState<boolean>(
    resetAction?.disabled ?? true
  );

  const [applyClicked, setApplyClicked] = useState(false);

  const selectionField = useMemo(() => {
    if (reportsConfig.selectionCriteria?.fields?.length > 0) {
      return reportsConfig.selectionCriteria.fields[0];
    }

    return null;
  }, [reportsConfig]);

  const selectionFieldWithoutRadio = useMemo(() => {
    if (reportsConfig.selectionCriteria?.fields?.length > 0) {
      return reportsConfig.selectionCriteria.fields.find(
        (field) => field.input_type !== 'radioGroup'
      );
    }

    return null;
  }, [reportsConfig]);

  const sentizedValues = useMemo(() => {
    const next = {...values, ...selectedSourcesBySection};

    const picked = _.pickBy(next, (v) => v !== null && v !== undefined);

    return picked;
  }, [values, selectedSourcesBySection]);

  const hasSelectionFields = useMemo(
    () => (reportsConfig.selectionCriteria?.fields?.length ?? 0) !== 0,
    [reportsConfig]
  );

  const selectedField = useMemo(
    () => reportsConfig.selectionCriteria?.fields,
    [reportsConfig]
  );

  useEffect(() => {
    onChange?.(sentizedValues);
  }, [sentizedValues, onChange]);

  useEffect(() => {
    if (validateActionFromParent) return;

    const hasAnyValue = (value: any) => {
      if (value === null || value === undefined || value === '' || !value)
        return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return Object.keys(value).length > 0;
      return true;
    };

    const isSourceVisible = Boolean(selectionFieldWithoutRadio);

    const isSourceSelected =
      isSourceVisible &&
      Object.values(selectedSourcesBySection).some((src) => hasAnyValue(src));

    const hasAnyFilterValue = Object.values(values).some((val) =>
      hasAnyValue(val)
    );

    const hasAnySelection = hasAnyFilterValue || isSourceSelected;

    const requiredComponents = reportsConfig.sections.flatMap((section) =>
      section.filters.filter((filter) => filter.isRequired)
    );

    const areRequiredFiltersFilled = requiredComponents.every((c) =>
      c.components.every((comp) => {
        const temp =
          comp?.key === 'timeOffset'
            ? true
            : comp.input_type === 'checkboxList'
              ? (Array.isArray(values[comp.key]) &&
                  values[comp.key].some(
                    (v: any) =>
                      v === true ||
                      typeof v === 'number' ||
                      typeof v === 'string'
                  )) ||
                (Array.isArray(comp.initialValues) &&
                  comp.initialValues.length > 0 &&
                  comp.initialValues.some((v: any) => v === true))
              : hasAnyValue(values[comp.key]);
        return temp;
      })
    );

    let applyDisabled = applyAction?.disabled ?? true;
    let resetDisabled = resetAction?.disabled ?? true;

    const mode = selectedOption?.value;

    if (mode === 'currentLoggedIn') {
      applyDisabled = false;
      resetDisabled = true;
    } else if (mode === 'loginHistory') {
      const hasSelection = areRequiredFiltersFilled || isSourceSelected;

      applyDisabled = !hasSelection;
      resetDisabled = !hasAnySelection;
    } else {
      applyDisabled = !hasAnySelection;
      resetDisabled = !hasAnySelection;
    }

    if (mode !== 'currentLoggedIn' && mode !== 'loginHistory') {
      if (isSourceVisible && !isSourceSelected) applyDisabled = true;
      if (!areRequiredFiltersFilled) applyDisabled = true;
    }

    const reviewerCountsField = reportsConfig.selectionCriteria?.fields?.find(
      (field) => field.key === 'selectedDailyHourly'
    );
    const isReviewerTimeOnline = reportsConfig.sections.some((section) =>
      section.filters.some((filter) =>
        filter.components.some((comp) => comp.key === 'timeOffset')
      )
    );
    const isUserReviewActivity = reportsConfig.sections.some((section) =>
      section.filters.some((filter) =>
        filter.components.some((comp) => comp.key === 'usersList')
      )
    );
    if (reviewerCountsField || isReviewerTimeOnline || isUserReviewActivity) {
      const reviewers = values.selectedReviewers ?? values.usersList;
      const reviewerSelected =
        Array.isArray(reviewers) && reviewers.some((item) => item === true);
      const requiredDateChanged = reportsConfig.sections
        .flatMap((section) => section.filters)
        .filter((filter) => filter.isRequired)
        .flatMap((filter) => filter.components)
        .some((comp) => {
          if (comp.input_type !== 'datePicker') return false;
          const current = String(values[comp.key] ?? '').trim();
          if (!current) return false;
          const initial = String(
            comp.initialValues || comp.initialStartDate || ''
          ).trim();
          if (!initial) return true;
          const currentDate = dayjs(current);
          const initialDate = dayjs(initial);
          return currentDate.isValid() && initialDate.isValid()
            ? !currentDate.isSame(initialDate)
            : current !== initial;
        });
      const selectionKey = (value: unknown) =>
        Array.isArray(value)
          ? value
              .map((item) => String(item ?? ''))
              .filter(Boolean)
              .sort()
              .join('|')
          : '';
      if (reviewerCountsField && loadedDailyHourlyRef.current === null) {
        loadedDailyHourlyRef.current = String(
          reviewerCountsField.value ?? '1'
        );
      }
      const selectedDailyHourly = reviewerCountsField
        ? String(values[reviewerCountsField.key] ?? '')
        : '';
      const dailyHourlyChanged =
        !!reviewerCountsField &&
        reviewerCountsField.key in values &&
        selectedDailyHourly !== '' &&
        selectedDailyHourly !== loadedDailyHourlyRef.current;
      const optionalChanged =
        !!(reviewerCountsField || isReviewerTimeOnline) &&
        reportsConfig.sections.some((section) =>
          section.filters.some(
            (filter) =>
              !filter.isRequired &&
              filter.components.some((comp) => {
                if (comp.input_type !== 'multiSelectTreeViewList') {
                  return false;
                }
                if (!(comp.key in values)) return false;
                return (
                  selectionKey(values[comp.key]) !==
                  selectionKey(comp.initialValues)
                );
              })
          )
        );
      const actionTypesChanged =
        isUserReviewActivity &&
        reportsConfig.sections.some((section) =>
          section.filters.some((filter) =>
            filter.components.some((comp) => {
              if (comp.key !== 'actionTypes' || !(comp.key in values)) {
                return false;
              }
              const current = values[comp.key];
              const baseline = Array.isArray(comp.childLabels)
                ? comp.childLabels.map((item: {value?: boolean}) =>
                    Boolean(item?.value)
                  )
                : [];
              return (
                Array.isArray(current) &&
                baseline.length > 0 &&
                current.length === baseline.length &&
                current.some(
                  (item, index) => Boolean(item) !== baseline[index]
                )
              );
            })
          )
        );
      resetDisabled = !(
        reviewerSelected ||
        requiredDateChanged ||
        optionalChanged ||
        dailyHourlyChanged ||
        actionTypesChanged
      );
      if (isUserReviewActivity) {
        applyDisabled = !areRequiredFiltersFilled;
      }
    }

    setIsApplyDisabled(applyDisabled);

    if (!applyClicked) {
      setIsResetDisabled(resetDisabled);
    }
  }, [
    selectedOption,
    values,
    selectedSourcesBySection,
    applyAction?.disabled,
    resetAction?.disabled,
    validateActionFromParent,
    reportsConfig,
    selectionField,
    JSON.stringify(values),
  ]);

  const isDateRangeRequired = () => {
    const requiredComponents = reportsConfig.sections.flatMap((section) =>
      section.filters.filter((filter) => filter.isRequired)
    );

    return requiredComponents.some(
      (c) => c.id === 'dateRangeSection' && c.isRequired
    );
  };

  /** Users childLabels patch (unchanged) */
  const patchUsersChildLabels = (comp: any) => {
    if (
      !(
        comp?.key === 'users' &&
        comp?.input_type === 'checkboxList' &&
        Array.isArray(comp?.childLabels)
      )
    ) {
      return comp;
    }

    const current = values.users;
    const labels = comp.childLabels.map((it: any) => it.label);

    let patched = comp.childLabels;

    if (
      Array.isArray(current) &&
      current.every((x) => typeof x === 'boolean') &&
      current.length === labels.length
    ) {
      patched = comp.childLabels.map((it: any, idx: number) => ({
        ...it,
        // eslint-disable-next-line security/detect-object-injection
        value: current[idx],
      }));
    } else if (
      Array.isArray(current) &&
      current.every((x) => typeof x === 'string')
    ) {
      const selectedSet = new Set(current);

      patched = comp.childLabels.map((it: any) => ({
        ...it,
        value: selectedSet.has(it.label) ? true : it.value,
      }));
    }

    return {...comp, childLabels: patched};
  };

  const patchExportFieldsChildLabels = (comp: any) => {
    if (
      !(comp?.input_type === 'checkboxList' && Array.isArray(comp?.childLabels))
    ) {
      return comp;
    }

    const exportFields = resolvedReportParams?.exportFields;
    if (!Array.isArray(exportFields) || exportFields.length === 0) {
      return comp;
    }

    const keyLower = String(comp?.key ?? '')
      .trim()
      .toLowerCase();

    const isMetadataList =
      keyLower.includes('metadata') ||
      keyLower === 'metadata' ||
      keyLower === 'metadatafields';

    const isWorkproductList =
      keyLower.includes('workproduct') ||
      keyLower === 'workproduct' ||
      keyLower === 'workproductfields';

    if (!isMetadataList && !isWorkproductList) {
      return comp;
    }

    const selectedIds = new Set(
      exportFields
        .filter((x: any) => {
          const t = String(x?.type ?? '')
            .trim()
            .toLowerCase();
          return isMetadataList
            ? t === 'metadata'
            : t === 'workproduct' || t === 'work_product';
        })
        .map((x: any) => String(x?.fieldId ?? ''))
        .filter(Boolean)
    );

    if (!selectedIds.size) {
      return comp;
    }

    const pickChildId = (c: any) =>
      String(c?.fieldId ?? c?.id ?? c?.value ?? c?.keyID ?? '');

    const patchedChildLabels = comp.childLabels.map((c: any) => {
      const id = pickChildId(c);
      return {...c, value: selectedIds.has(id)};
    });

    return {...comp, childLabels: patchedChildLabels, disabled: true};
  };

  const handleActionClick = (actionKey: string, actionLabel: string) => {
    //Skip date validation if parent component handles it
    if (
      !skipDateValidation &&
      actionLabel === 'Apply' &&
      isDateRangeRequired()
    ) {
      dayjs.extend(utc);
      const fromDateFormat =
        sentizedValues?.fromDate ?? sentizedValues?.fromDateTime;
      const toDateFormat = sentizedValues?.toDate ?? sentizedValues?.toDateTime;

      const fromDate = dayjs.utc(fromDateFormat);
      const toDate = dayjs.utc(toDateFormat);
      const today = dayjs.utc();

      if (fromDate.isAfter(today)) {
        return setToastMessage(t('common.fromDateNotInFuture'));
      }
      if (toDate.isAfter(today)) {
        return setToastMessage(t('common.toDateNotInFuture'));
      }
      if (fromDate.isBefore(toDate) || fromDate.isSame(toDate)) {
        console.warn('valid ranges');
      } else {
        return setToastMessage(t('common.dateRange'));
      }
    }

    if (actionLabel === 'Apply') {
      setApplyClicked(true);
      setIsResetDisabled(false);
    } else if (actionLabel === 'Reset') {
      setApplyClicked(false);
      setIsResetDisabled(true);
    }

    onAction?.(actionKey, sentizedValues);
  };

  const getDisabledState = (action: any) => {
    if (validateActionFromParent) {
      return action.disabled;
    }

    if (action.label === 'Apply') {
      return isApplyDisabled;
    }

    if (action.label === 'Reset') {
      return isResetDisabled;
    }

    return action.disabled;
  };

  useEffect(() => {
    const type = resolvedReportParams?.sourceType;
    const ids = resolvedReportParams?.sourceID;

    if (!type || ids === undefined || ids === null) return;

    const sectionId = mapTypeToSectionId(String(type));
    const sectionMeta = meta(sectionId);
    const sourceIds = toArray(ids);

    const aggregated = {
      [sectionId]: [
        {
          sourceName: sectionMeta.sourceName,
          source: sourceIds,
          navigationSourceType: sectionMeta.navigationSourceType,
        },
      ],
    };

    setSelectedSourcesBySection((prev) => ({...prev, ...aggregated}));
  }, [
    resolvedReportParams?.sourceType,
    resolvedReportParams?.sourceID,
    sources,
  ]);

  return (
    <Box>
      {/* Selection Criteria header + actions */}
      <Box className="selection-criteria-box">
        <FilterHeader
          title={reportsConfig.selectionCriteria.title}
          showInfoIcon={reportsConfig.selectionCriteria.showInfoIcon}
          tooltipText={reportsConfig.selectionCriteria.tooltipText}
          tooltipTitle={reportsConfig.selectionCriteria.tooltipTitle}
          forceInLineToolTip={
            reportsConfig.selectionCriteria.forceInLineToolTip
          }
          isRequired={reportsConfig.selectionCriteria.isRequired}
        />
        <Box className="selection-criteria-content">
          {hasSelectionFields && selectedField[0]?.input_type === 'toggle' ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1rem',
              }}
            >
              <Typography
                className="typo-text subhead5_size"
                sx={{color: '#ffffff'}}
                data-testid={`filter-section-toggle-label`}
              >
                {selectedField[0].label}
              </Typography>
              <ArrToggle
                value={
                  values[selectedField[0].key] !== null &&
                  values[selectedField[0].key] !== undefined
                    ? values[selectedField[0].key]
                    : (selectedField[0].value ?? true)
                }
                toggleSize={selectedField[0].toggleSize || 'medium'}
                onChange={(event, checked) => {
                  const newValue =
                    checked !== undefined
                      ? checked
                      : (event?.target?.checked ?? false);
                  handleChange(selectedField[0].key, newValue);
                }}
                ariaLabel={selectedField[0].label}
                dataTestId={`toggle-${selectedField[0].key}`}
              />
            </Box>
          ) : null}
          <Box className="button-box">
            {reportsConfig.selectionCriteria.actions.map((action) => {
              const ActionComponent = componentRegistry[action.input_type];

              return (
                <ActionComponent
                  key={action.label}
                  label={action.label}
                  variant={action.variant}
                  size={action.size}
                  type="submit"
                  aria-label={action['aria-label']}
                  data-testid={action['data-testid']}
                  className={action.className}
                  input_type={action.input_type}
                  onClick={() =>
                    handleActionClick(action.actionKey, action.label)
                  }
                  disabled={getDisabledState(action)}
                />
              );
            })}
          </Box>
          {/* Render fields (like toggle) above buttons */}
          {hasSelectionFields &&
          selectedField[0]?.input_type === 'textField' ? (
            <>
              <Box
                style={{
                  paddingTop: '1.5rem',
                  paddingRight: '15rem',
                  marginRight: '500rem',
                }}
              >
                <Box display="flex" alignItems="center">
                  <Typography data-testid={`filter-section-text-field-label`}>
                    {selectedField[0].label}
                  </Typography>
                  {selectedField[0]?.showInfoIcon && (
                    <ArrTooltipWrapper
                      trigger={<InfoIcon className="info_icon_small" />}
                    >
                      <Typography
                        className="typo-text body4_size"
                        data-testid="export-data-tooltip"
                      >
                        {selectedField[0]?.tooltipText}
                      </Typography>
                    </ArrTooltipWrapper>
                  )}
                </Box>
              </Box>
              <ArrTextField
                id={selectedField[0].key}
                value={values[selectedField[0].key] ?? ''}
                dataTestId={`filter-section-${selectedField[0].key}`}
                onChange={(e) =>
                  handleChange(selectedField[0].key, e.target.value)
                }
                slotProps={{input: {style: {height: '3rem'}}}}
                sx={{
                  '& .MuiFilledInput-input': {
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important',
                  },
                }}
              />
            </>
          ) : hasSelectionFields &&
            selectedField[0]?.input_type === 'radioGroup' ? (
            <Box className="radio-box">
              <ArrRadio
                dataTestId={selectedField[0].key}
                name={selectedField[0].key}
                value={
                  values[selectedField[0].key] ?? selectedField[0].value ?? '1'
                }
                onChange={(e: string) => handleChange(selectedField[0].key, e)}
                data={selectedField[0].options || []}
                size="small"
              />
            </Box>
          ) : hasSelectionFields &&
            selectedField[0]?.input_type !== 'toggle' ? (
            <Box className="source-box">
              <Box className="source-box-heading">
                <Typography
                  className="source-box-heading-text"
                  tabIndex={0}
                  data-testid={`filter-section-sources`}
                >
                  Sources
                  <span className="asterix" data-testid={`filter-section-`}>
                    *
                  </span>
                </Typography>
              </Box>
              <Box className="source-box-dropdown">
                <ArrSourceSelectedSection
                  sources={sources}
                  disableSourceSelection={disableSourceSelection}
                  onSelectionsUpdate={(update) => {
                    setSelectedSourcesBySection(update);
                  }}
                  sourcesDropdownSize={'medium'}
                  multiSelectSources={selectionField?.multiSelect}
                  resetSignal={resetSignal}
                  initialSelectedSources={{
                    ...initialSelectedSources,
                    ...initialSelectedBySectionFromResolved,
                  }}
                  sortSecurity={true}
                />
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
      {/* Dynamic Sections */}
      <Box>
        <Box>
          {reportsConfig.sections.map((section, _idx) => (
            <Box key={`${section.title}-${_idx}`}>
              {section.title && (
                <Box className="selection-criteria-box" marginTop="10px">
                  <FilterHeader
                    title={section.title}
                    showInfoIcon={section.showInfoIcon}
                    tooltipText={section.tooltipText}
                    tooltipTitle={section.tooltipTitle}
                    isRequired={section.isRequired}
                  />
                </Box>
              )}
              {section.filters.map((filter, _) => {
                const accordionSections = [
                  {
                    id: filter.id,
                    title: filter.title,
                    showInfoIcon: filter.showInfoIcon,
                    tooltipText: filter.tooltipText,
                    tooltipTitle: filter.tooltipTitle,
                    isRequired: filter.isRequired,
                    infoIconClass: 'info_icon_small',
                    content: (
                      <Box
                        className={classNames(
                          'arr-accordion-container',
                          filter.className,
                          {
                            'arr-accordion-date-container':
                              filter.id === 'dateRangeSection',
                          }
                        )}
                      >
                        {filter.components.map((comp) => {
                          if (comp.input_type === 'datePicker') {
                            const {
                              label,
                              value: _ignoreDefault,
                              ...rest
                            } = comp;

                            const lower = String(label ?? '')
                              .trim()
                              .toLowerCase();

                            const rawFrom =
                              values['fromDate'] ??
                              values['fromdate'] ??
                              resolvedReportParams?.fromDateTime ??
                              '';

                            const rawTo =
                              values['toDate'] ??
                              values['todate'] ??
                              resolvedReportParams?.toDateTime ??
                              '';

                            const valFrom = normalizeStrict(rawFrom);
                            const valTo = normalizeStrict(rawTo);

                            const val = normalizeStrict(
                              lower === 'from'
                                ? (values[comp.key] ??
                                    resolvedReportParams?.fromDateTime ??
                                    '')
                                : (values[comp.key] ??
                                    resolvedReportParams?.toDateTime ??
                                    '')
                            );

                            let maxDateTime = dayjs.utc(valTo);
                            let minDateTime = dayjs.utc(valFrom);

                            let isFromDateValue = valFrom;
                            if (!valFrom && !valTo) {
                              const initialFromDate = filter.components.find(
                                (item: any) =>
                                  item.key === 'fromDate' ||
                                  item.key === 'fromDateTime'
                              ) as unknown as any;
                              const initialToDate = filter.components.find(
                                (item: any) =>
                                  item.key === 'toDate' ||
                                  item.key === 'toDateTime'
                              ) as unknown as any;

                              const fromDateValue =
                                initialFromDate?.initialStartDate ?? undefined;
                              const toDateValue =
                                initialToDate?.initialStartDate ??
                                initialToDate?.initialEndDate ??
                                undefined;

                              maxDateTime = dayjs.utc(toDateValue);
                              minDateTime = dayjs.utc(fromDateValue);
                              isFromDateValue = fromDateValue;
                            }

                            const forcedComp: any =
                              lower === 'from'
                                ? {
                                    ...rest,
                                    selectedDate: valFrom,
                                    maxDateTime,
                                    dataTestId:
                                      filter.title
                                        .replaceAll(' ', '-')
                                        .toLowerCase() + '-from',
                                  }
                                : {
                                    ...rest,
                                    selectedDate: valTo,
                                    minDateTime: isFromDateValue
                                      ? minDateTime
                                      : undefined,
                                    maxDateTime,
                                    dataTestId:
                                      filter.title
                                        .replaceAll(' ', '-')
                                        .toLowerCase() + '-to',
                                  };

                            return (
                              <Box
                                key={comp.key}
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Typography
                                  className="typo-text body3_size date-label"
                                  tabIndex={0}
                                  data-testid={`filter-section-typography-label-${filter.id}-${comp.key}`}
                                >
                                  {label}
                                </Typography>
                                <Box
                                  className={'arr-date-picker-container'}
                                  sx={{
                                    '& .MuiInputBase-input': {
                                      padding: '0px 14px !important',
                                    },
                                  }}
                                >
                                  <LoadInputType
                                    key={`${comp.key}-${resetSignal || resetDatePicker ? 'reset' : 'normal'}`}
                                    comp={forcedComp}
                                    value={val}
                                    onChange={(v) => handleChange(comp.key, v)}
                                  />
                                </Box>
                              </Box>
                            );
                          }

                          const effectiveComp =
                            comp?.key === 'users' &&
                            comp?.input_type === 'checkboxList'
                              ? patchUsersChildLabels(comp)
                              : patchExportFieldsChildLabels(comp);

                          return (
                            <LoadInputType
                              key={comp.key}
                              comp={effectiveComp}
                              value={values[comp.key] ?? ''}
                              onChange={(val) => handleChange(comp.key, val)}
                            />
                          );
                        })}
                      </Box>
                    ),
                  },
                ];

                return filter.showAccordion ? (
                  <Box
                    className={
                      filter.sectionClassName
                        ? `standard-filter-section ${filter.sectionClassName}`
                        : 'standard-filter-section'
                    }
                    marginTop="3px"
                    key={filter.id}
                  >
                    <ArrAccordion
                      key={resetSignal ? 'reset' : 'normal'}
                      sections={accordionSections}
                      expandedSection={
                        filter.isDefaultExpand ? filter.id : undefined
                      }
                      onToggle={() => {}}
                      allowMultiple={false}
                      dataTestId={`filter-section-${filter.id}`}
                    />
                  </Box>
                ) : (
                  <Box
                    className={
                      filter?.id === 'exportData'
                        ? 'exportFormatContainer'
                        : 'inputContainer'
                    }
                  >
                    {filter.components.map((comp) => {
                      if (comp.input_type === 'datePicker') {
                        const {label, value: _ignoreDefault, ...rest} = comp;

                        const lower = String(label ?? '')
                          .trim()
                          .toLowerCase();

                        const rawFrom =
                          values['fromDate'] ??
                          values['fromdate'] ??
                          resolvedReportParams?.fromDateTime ??
                          '';

                        const rawTo =
                          values['toDate'] ??
                          values['todate'] ??
                          resolvedReportParams?.toDateTime ??
                          '';

                        const valFrom = normalizeStrict(rawFrom);
                        const valTo = normalizeStrict(rawTo);

                        const val = normalizeStrict(
                          lower === 'from'
                            ? (values[comp.key] ??
                                resolvedReportParams?.fromDateTime ??
                                '')
                            : (values[comp.key] ??
                                resolvedReportParams?.toDateTime ??
                                '')
                        );

                        const forcedComp: any =
                          lower === 'from'
                            ? {...rest, selectedDate: valFrom}
                            : {...rest, selectedDate: valTo};

                        return (
                          <LoadInputType
                            key={`${comp.key}-${resetSignal || resetDatePicker ? 'reset' : 'normal'}`}
                            comp={forcedComp}
                            value={val}
                            onChange={(v) => handleChange(comp.key, v)}
                          />
                        );
                      }

                      const effectiveComp =
                        comp?.key === 'users' &&
                        comp?.input_type === 'checkboxList'
                          ? patchUsersChildLabels(comp)
                          : patchExportFieldsChildLabels(comp);

                      return (
                        <LoadInputType
                          key={`${comp.key}-${resetSignal ? 'reset' : 'normal'}`}
                          comp={effectiveComp}
                          value={values[comp.key] ?? ''}
                          onChange={(val) => handleChange(comp.key, val)}
                        />
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
      {toastMessage && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => setToastMessage(undefined)}
          status={[ToastType.Warning]}
        />
      )}
    </Box>
  );
};

export default FilterSection;
